'use client';

import { API_ROUTES, HTTP_METHODS, useApiMutation } from '@shared/api-utils';
import { Button, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@shared/components/shadcn';
import { getSiteConfig } from '@shared/config';
import { createDebug } from '@shared/debug';
import { ChevronLeft } from '@shared/icons';
import { ROUTES, useClientConfig } from '@shared/utils';
import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useEffectEvent, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { MultistepFormProvider } from '../../../form-controls/MultistepForm/context/MultistepFormContext';
import { useMultistepFormState } from '../../../form-controls/MultistepForm/hooks/useMultistepFormState';
import { BackHandler } from './components/BackHandler';
import { ErrorStep } from './components/ErrorStep';
import { SkipVerificationDialog } from './components/SkipVerificationDialog';
import { useKycDialog } from './context/KycDialogContext';
import { useKycStartMethod } from './hooks/useKycStartMethod';
import { ManualUploadStep } from './steps/ManualUpload/ManualUploadStep';
import { VerificationMethodsStep } from './steps/VerificationMethods/VerificationMethodsStep';
import type { StartVerificationFlowRequest, StartVerificationFlowResponse } from './types';

// Loaded client-side only: the Jumio web SDK references `HTMLElement` at module-eval time,
// which breaks static prerendering (SSG/ISR) in Node. Keeping it out of the server graph
// avoids "ReferenceError: HTMLElement is not defined" during `next build`.
const JumioStep = dynamic(() => import('./steps/Jumio/JumioStep').then(module => module.JumioStep), { ssr: false });

// GreenID injects its SDK into `window` and the DOM, so it is also kept out of the server graph.
const GreenIdStep = dynamic(() => import('./steps/GreenId/GreenIdStep').then(module => module.GreenIdStep), { ssr: false });

/**
 * Displays and controls the KYC Verification dialog.
 *
 * Behaviour overview:
 * - Opens programmatically via KycDialogContext (useKycDialog hook).
 * - Can be triggered after login when verification check returns isVerified: false.
 * - Can be triggered on user actions (e.g., clicking Deposit CTA when not verified).
 * - Uses MultistepForm to guide the user through verification flow.
 * - Fetches available verification methods and displays them as methods.
 * - Handles starting verification flows (Jumio or Manual Uploads).
 *
 * Usage:
 * - Wrap KycDialog with KycDialogProvider.
 * - Automatically opens after login when verification check returns isVerified: false.
 * - Use useKycDialog().openKycDialog() to open from user actions (e.g., clicking Deposit CTA when not verified).
 */
function KycDialog() {
  const [showBackButton, setShowBackButton] = useState(false);
  const [isSkipVerificationDialogOpen, setIsSkipVerificationDialogOpen] = useState(false);
  const [hasError, setHasError] = useState(false);
  const skipToStepRef = useRef<((stepId: string) => void) | null>(null);
  const pendingRegistrationSkipRef = useRef(false);
  const config = useClientConfig();
  const t = useTranslations('global_dialogs.kyc_verification');
  const tCommon = useTranslations('common');
  const {
    isKycDialogOpen,
    closeKycDialog,
    openKycDialog,
    shouldShowCloseButton,
    shouldShowBackButton: contextShouldShowBackButton,
    showCloseButton,
    isRegistrationFlow,
    hasOnCompleteCallback,
    isNameChangeFlow,
    notifyKycSkipped,
    dataTrackFeature,
    dataTrackPrivacy,
    startMethod,
  } = useKycDialog();
  const { nameChangeKycDialogTitleKey, nameChangeSkipVerificationRedirectPath, isUseNameChangeManualUploadCopy } = getSiteConfig().PROFILE_INFO;

  // Combine local and context back button visibility
  const shouldShowBackButton = showBackButton && contextShouldShowBackButton;

  // Note: Auto-trigger disabled - dialog opens programmatically when needed with openKycDialog() (e.g., during registration when GBG fails)
  // useKycDialogTrigger();

  const debug = createDebug({ isEnabled: true });

  const form = useForm<{ verificationOption?: StartVerificationFlowResponse }>({
    mode: 'all',
    defaultValues: {
      verificationOption: undefined,
    },
  });

  const { mutateAsync: startVerificationFlow } = useApiMutation<StartVerificationFlowResponse, StartVerificationFlowRequest>(
    API_ROUTES.PLAYER_KYC.VERIFICATION.START_FLOW,
    HTTP_METHODS.POST
  );

  const openSkipVerificationDialog = () => {
    if (isRegistrationFlow) {
      pendingRegistrationSkipRef.current = true;
    }
    setShowBackButton(false);
    closeKycDialog();
    setIsSkipVerificationDialogOpen(true);
  };

  const handleOpenChange = (openState: boolean) => {
    if (!openState) {
      openSkipVerificationDialog();
    }
  };

  const handleSelectMethod = async (flowId: string): Promise<StartVerificationFlowResponse | null> => {
    try {
      debug.log('[KYC Dialog] Starting verification flow with flowId:', flowId);

      const response = await startVerificationFlow({ flowId });

      debug.log('[KYC Dialog] Verification flow started:', response);

      form.setValue('verificationOption', response as StartVerificationFlowResponse);
      setHasError(false);
      return response;
    } catch (error) {
      debug.forceError('[KYC Dialog] Failed to start verification flow:', error);
      setHasError(true);
      return null;
    }
  };

  // Runs the method requested through `startMethod` (e.g. GreenID) straight away, skipping the method selection
  const {
    stepId: startMethodStepId,
    restart: restartStartMethod,
    closeFlow: closeStartMethodFlow,
  } = useKycStartMethod({
    isKycDialogOpen,
    startMethod,
    startFlow: handleSelectMethod,
    clearFlow: () => form.setValue('verificationOption', undefined),
    setHasError,
  });

  const handleDoItLater = () => {
    openSkipVerificationDialog();
  };

  const handleSkipVerificationContinue = () => {
    setIsSkipVerificationDialogOpen(false);
    pendingRegistrationSkipRef.current = false;
    setShowBackButton(false);
    form.reset();
    // Re-opening must keep the start method, otherwise the dialog would fall back to the method selection
    openKycDialog(startMethod ? { startMethod } : undefined);
  };

  const handleSkipVerificationDoItLater = () => {
    const shouldRedirectToRegistrationProfile = pendingRegistrationSkipRef.current && !hasOnCompleteCallback;

    setIsSkipVerificationDialogOpen(false);
    pendingRegistrationSkipRef.current = false;

    // Read before the reset: it identifies the flow to close. Without it the flow never started, so there is nothing to close.
    const startedFlowOperationId = form.getValues('verificationOption')?.operationId;
    form.reset();
    closeStartMethodFlow(startedFlowOperationId);

    if (isNameChangeFlow) {
      notifyKycSkipped();

      if (nameChangeSkipVerificationRedirectPath) {
        window.location.href = nameChangeSkipVerificationRedirectPath;
      }

      return;
    }

    closeKycDialog();

    if (!shouldRedirectToRegistrationProfile) return;

    window.location.href = `${config.register.redirectUrl}${ROUTES.REGISTER_REDIRECTION_SUCCESS}`;
  };

  const handleStepInfo = useCallback((stepIndex: number, goBack: () => void, skipToStep: (stepId: string) => void) => {
    // Show back button on every step after the method selection (jumio, manual-upload and greenid)
    // Step indices: 0=verification-methods, 1=jumio, 2=manual-upload, 3=greenid
    // Steps that must not offer going back (e.g. greenid) never enable it in the context
    // Close button visibility is managed by each step component
    const showBack = stepIndex >= 1;
    setShowBackButton(showBack);
    skipToStepRef.current = skipToStep;
  }, []);

  const handleBackClick = () => {
    // Always navigate back to verification-methods step, regardless of current step
    if (skipToStepRef.current) {
      skipToStepRef.current('verification-methods');
    }
  };

  const handleErrorRetry = () => {
    debug.log('[KYC Dialog] Retrying after error');
    showCloseButton();

    if (startMethodStepId) {
      skipToStepRef.current?.(startMethodStepId);
      void restartStartMethod();
      return;
    }

    setHasError(false);
    form.reset();
    if (skipToStepRef.current) {
      skipToStepRef.current('verification-methods');
    }
  };

  const handleMultistepFormReady = useCallback((skipToStep: (stepId: string) => void) => {
    skipToStepRef.current = skipToStep;
  }, []);

  // Reset to the first step when dialog opens. A start method (e.g. GreenID) skips the method selection.
  const resetToFirstStep = useEffectEvent(() => {
    if (!skipToStepRef.current) return;

    skipToStepRef.current(startMethodStepId ?? 'verification-methods');
    if (startMethodStepId) form.setValue('verificationOption', undefined);

    setShowBackButton(false);
    setHasError(false);
  });

  useEffect(() => {
    if (isKycDialogOpen) resetToFirstStep();
  }, [isKycDialogOpen]);

  // Show close button and hide back button when error is displayed
  useEffect(() => {
    if (hasError) {
      showCloseButton();
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShowBackButton(false);
    }
  }, [hasError, showCloseButton]);

  const steps = [
    {
      id: 'verification-methods',
      component: (
        <>
          <BackHandler onStepInfo={handleStepInfo} />
          <VerificationMethodsStep onSelectMethod={handleSelectMethod} onDoItLater={handleDoItLater} />
        </>
      ),
      dataTrackContainer: 'Method Selection',
      schema: z.any(),
    },
    {
      id: 'jumio',
      component: (
        <>
          <BackHandler onStepInfo={handleStepInfo} />
          <JumioStep />
        </>
      ),
      dataTrackContainer: 'Jumio',
      schema: z.any(),
    },
    {
      id: 'manual-upload',
      component: (
        <>
          <BackHandler onStepInfo={handleStepInfo} />
          <ManualUploadStep />
        </>
      ),
      dataTrackContainer: 'Document Upload',
      schema: z.any(),
    },
    {
      id: 'greenid',
      component: (
        <>
          <BackHandler onStepInfo={handleStepInfo} />
          <GreenIdStep onRestart={restartStartMethod} />
        </>
      ),
      dataTrackContainer: 'GreenID',
      schema: z.any(),
    },
  ];

  const formState = useMultistepFormState({
    steps,
    onComplete: () => {},
    onMultistepFormReady: handleMultistepFormReady,
  });

  const getDialogTitleKey = () => {
    if (!isNameChangeFlow) return 'title';
    if (isUseNameChangeManualUploadCopy && formState.currentStep?.id === 'manual-upload') return 'name_change.upload_title';
    return nameChangeKycDialogTitleKey;
  };

  const dialogTitleKey = getDialogTitleKey();

  return (
    <>
      <Dialog open={isKycDialogOpen} onOpenChange={handleOpenChange}>
        <DialogContent
          className="z-1000 overflow-hidden"
          showCloseButton={shouldShowCloseButton}
          onInteractOutside={e => e.preventDefault()}
          onEscapeKeyDown={e => e.preventDefault()}
          onOpenAutoFocus={e => e.preventDefault()}
          data-track-feature={dataTrackFeature ?? 'Verification'}
          {...(dataTrackPrivacy && { 'data-track-privacy': dataTrackPrivacy })}
          data-testid="kyc-dialog"
        >
          <div key={`kyc-step-${formState.currentStepIndex}-wrapper`} className="flex flex-col flex-1 min-h-0" data-track-container={formState.currentStep?.dataTrackContainer}>
            <DialogHeader className="relative flex items-center justify-center px-5 border-b-1 border-border">
              {shouldShowBackButton && (
                <Button
                  variant="secondary"
                  onClick={handleBackClick}
                  className="absolute -mt-2 left-5 top-1/2 -translate-y-1/2 flex items-center justify-center w-8 h-8 rounded-full p-0"
                  type="button"
                  aria-label={tCommon('back')}
                  data-track-name="Back"
                  data-testid="dialog-back-button"
                >
                  <ChevronLeft size="small" />
                </Button>
              )}
              <DialogTitle>{t(dialogTitleKey)}</DialogTitle>
              <DialogDescription className="sr-only">{t(isNameChangeFlow ? 'name_change.heading' : 'heading')}</DialogDescription>
            </DialogHeader>

            <FormProvider {...form}>
              <MultistepFormProvider key={`kyc-step-${formState.currentStepIndex}`} value={formState}>
                <div className="flex flex-1 flex-col min-h-0">{hasError ? <ErrorStep onRetry={handleErrorRetry} /> : formState.currentStep?.component}</div>
              </MultistepFormProvider>
            </FormProvider>
          </div>
        </DialogContent>
      </Dialog>
      <SkipVerificationDialog isOpen={isSkipVerificationDialogOpen} onContinueVerification={handleSkipVerificationContinue} onDoItLater={handleSkipVerificationDoItLater} />
    </>
  );
}

export { KycDialog };
